import {REPORT_GROUPINGS} from '../../../../constants';

/** The groupings the standard task report page can render at each level, as its two grouping
 * dropdowns offer them. Kept as data rather than read off the dropdown builders because those need
 * `intl` and feature-flag state, and this has to be usable when seeding page state before either
 * exists.
 *
 * The second level depends on the first: the page never offers the same grouping twice, and not
 * every pair has a relay wrapper behind it. */
const FIRST_LEVEL: ReadonlySet<string> = new Set([
	REPORT_GROUPINGS.PROJECT,
	REPORT_GROUPINGS.PHASE,
	REPORT_GROUPINGS.CLIENT,
	REPORT_GROUPINGS.ROLE,
]);

const SECOND_LEVEL_BY_FIRST: Readonly<Record<string, ReadonlySet<string>>> = {
	[REPORT_GROUPINGS.PHASE]: new Set([REPORT_GROUPINGS.PROJECT, REPORT_GROUPINGS.CLIENT, REPORT_GROUPINGS.ROLE]),
	[REPORT_GROUPINGS.PROJECT]: new Set([REPORT_GROUPINGS.PHASE, REPORT_GROUPINGS.ROLE, REPORT_GROUPINGS.SPRINT]),
	[REPORT_GROUPINGS.CLIENT]: new Set([
		REPORT_GROUPINGS.PROJECT,
		REPORT_GROUPINGS.ROLE,
		REPORT_GROUPINGS.PHASE,
		REPORT_GROUPINGS.SPRINT,
	]),
	[REPORT_GROUPINGS.ROLE]: new Set([
		REPORT_GROUPINGS.PROJECT,
		REPORT_GROUPINGS.CLIENT,
		REPORT_GROUPINGS.PHASE,
		REPORT_GROUPINGS.SPRINT,
	]),
};

export type TaskReportGroupingPair = [first: string | null, second: string | null];

/** Reduces a stored grouping pair to one the standard task report page can render.
 *
 * A saved task report can carry groupings this page has no dropdown option, relay query or
 * component for: the AI report builder writes its own grouping vocabulary (assignee, deliverable,
 * and the project-status tokens) into the same columns, and a report built there can be opened here
 * through a share link or the standard saved-report route. Fed such a token, the page's query
 * selector falls through to a placeholder string, which Relay rejects during render and the whole
 * page crashes. An unknown grouping is not something this page can honour, so it is dropped to
 * "no grouping" — the flat task list — which is what the page shows for a report that was never
 * grouped. A second-level grouping is dropped too when it is unknown or when the first level was,
 * since it is only offered relative to the first. */
export function normaliseTaskReportGroupings(
	first: string | null | undefined,
	second: string | null | undefined
): TaskReportGroupingPair {
	const firstLevel = typeof first === 'string' && FIRST_LEVEL.has(first) ? first : null;
	if (firstLevel === null) {
		return [null, null];
	}
	const allowedSecond = SECOND_LEVEL_BY_FIRST[firstLevel];
	const secondLevel = typeof second === 'string' && allowedSecond.has(second) ? second : null;
	return [firstLevel, secondLevel];
}
